import api from "./axiosConfig";


const compactParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([_key, value]) => value !== undefined && value !== null && value !== "")
  );

export const AUTOPILOT_OVERVIEW_TTL_MS = 45 * 1000;

const overviewCache = new Map();
const overviewInflight = new Map();
let overviewCacheVersion = 0;

const buildOverviewCacheKey = (params = {}) => {
  const normalizedParams = compactParams(params);
  return JSON.stringify(
    Object.keys(normalizedParams)
      .sort()
      .map((key) => [key, normalizedParams[key]])
  );
};

export const invalidateAutopilotOverviewCache = () => {
  overviewCache.clear();
  overviewInflight.clear();
  overviewCacheVersion += 1;
};

export const getAutopilotOverview = (params = {}, { force = false } = {}) => {
  const normalizedParams = compactParams(params);
  const cacheKey = buildOverviewCacheKey(normalizedParams);
  const now = Date.now();

  if (!force) {
    const cached = overviewCache.get(cacheKey);
    if (cached && cached.expiresAt > now) {
      return Promise.resolve(cached.response);
    }

    const inflight = overviewInflight.get(cacheKey);
    if (inflight) {
      return inflight;
    }
  }

  const requestVersion = overviewCacheVersion;
  const requestPromise = api
    .get("/api/autopilot/overview/", { params: normalizedParams })
    .then((response) => {
      if (requestVersion === overviewCacheVersion) {
        overviewCache.set(cacheKey, {
          response,
          expiresAt: Date.now() + AUTOPILOT_OVERVIEW_TTL_MS,
        });
      }
      return response;
    })
    .finally(() => {
      if (overviewInflight.get(cacheKey) === requestPromise) {
        overviewInflight.delete(cacheKey);
      }
    });

  overviewInflight.set(cacheKey, requestPromise);
  return requestPromise;
};

const withOverviewInvalidation = (requestFactory) => async (...args) => {
  const response = await requestFactory(...args);
  invalidateAutopilotOverviewCache();
  return response;
};


export const autopilotApi = {
  overview: (params = {}, options = {}) => getAutopilotOverview(params, options),
  policies: (params = {}) => api.get("/api/autopilot/policies/", { params: compactParams(params) }),
  savePolicy: withOverviewInvalidation((payload) => api.put("/api/autopilot/policies/", payload)),
  communicationPolicy: (params = {}) => api.get("/api/autopilot/communication-policy/", { params: compactParams(params) }),
  saveCommunicationPolicy: withOverviewInvalidation((payload) => api.put("/api/autopilot/communication-policy/", payload)),
  recipientBlocks: (params = {}) => api.get("/api/autopilot/recipient-blocks/", { params: compactParams(params) }),
  createRecipientBlock: withOverviewInvalidation((payload) => api.post("/api/autopilot/recipient-blocks/", payload)),
  updateRecipientBlock: withOverviewInvalidation((id, payload) => api.patch(`/api/autopilot/recipient-blocks/${id}/`, payload)),
  deleteRecipientBlock: withOverviewInvalidation((id) => api.delete(`/api/autopilot/recipient-blocks/${id}/`)),
  templates: (params = {}) => api.get("/api/autopilot/templates/", { params: compactParams(params) }),
  createTemplate: withOverviewInvalidation((payload) => api.post("/api/autopilot/templates/", payload)),
  updateTemplate: withOverviewInvalidation((id, payload) => api.patch(`/api/autopilot/templates/${id}/`, payload)),
  deleteTemplate: withOverviewInvalidation((id) => api.delete(`/api/autopilot/templates/${id}/`)),
  previewTemplate: (id, payload = {}) => api.post(`/api/autopilot/templates/${id}/preview/`, payload),
  seedDefaultTemplates: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/templates/seed-defaults/", payload)),
  seedTwilioReclamosTemplates: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/templates/seed-twilio-reclamos/", payload)),
  actions: (params = {}) => api.get("/api/autopilot/actions/", { params: compactParams(params) }),
  approvals: (params = {}) => api.get("/api/autopilot/approvals/", { params: compactParams(params) }),
  risks: (params = {}) => api.get("/api/autopilot/risks/", { params: compactParams(params) }),
  correoEmailContext: (emailId) => api.get(`/api/autopilot/correo/email/${emailId}/context/`),
  applyTemplate: withOverviewInvalidation((id, payload) => api.post(`/api/autopilot/actions/${id}/apply-template/`, payload)),
  approveAction: withOverviewInvalidation((id, payload = {}) => api.post(`/api/autopilot/actions/${id}/approve/`, payload)),
  executeAction: withOverviewInvalidation((id, payload = {}) => api.post(`/api/autopilot/actions/${id}/execute/`, payload)),
  sendReceipt: withOverviewInvalidation((id, payload = {}) => api.post(`/api/autopilot/actions/${id}/send-receipt/`, payload)),
  checkWhatsAppGuard: (id, payload = {}) => api.post(`/api/autopilot/actions/${id}/check-whatsapp-guard/`, payload),
  sendWhatsApp: withOverviewInvalidation((id, payload = {}) => api.post(`/api/autopilot/actions/${id}/send-whatsapp/`, payload)),
  rejectAction: withOverviewInvalidation((id, payload = {}) => api.post(`/api/autopilot/actions/${id}/reject/`, payload)),
  dismissAction: withOverviewInvalidation((id) => api.post(`/api/autopilot/actions/${id}/dismiss/`, {})),
  ackRisk: withOverviewInvalidation((id) => api.post(`/api/autopilot/risks/${id}/ack/`, {})),
  dismissRisk: withOverviewInvalidation((id, payload = {}) => api.post(`/api/autopilot/risks/${id}/dismiss/`, payload)),
  scanReclamos: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/scan/reclamos/", payload)),
  scanGastos: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/scan/gastos/", payload)),
  scanGastosRecurrentes: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/scan/gastos-recurrentes/", payload)),
  scanCorreo: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/scan/correo/", payload)),
  scanCobranzas: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/scan/cobranzas/", payload)),
  scanConciliacion: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/scan/conciliacion/", payload)),
  scanLiquidacionPreflight: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/scan/liquidacion-preflight/", payload)),
  scanLiquidacionPreview: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/scan/liquidacion-preview/", payload)),
  scanLiquidacionParams: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/scan/liquidacion-parametros/", payload)),
  previewBulkConfirmGastos: (payload = {}) => api.post("/api/autopilot/gastos/bulk-confirm/preview/", payload),
  executeBulkConfirmGastos: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/gastos/bulk-confirm/execute/", payload)),
  previewBulkConciliacion: (payload = {}) => api.post("/api/autopilot/conciliacion/bulk-reconcile/preview/", payload),
  executeBulkConciliacion: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/conciliacion/bulk-reconcile/execute/", payload)),
  generateMonthlyClosingChecklist: withOverviewInvalidation((payload = {}) => api.post("/api/autopilot/cierre-mensual/generate/", payload)),
  monthlyClosingChecklists: (params = {}) => api.get("/api/autopilot/cierre-mensual/", { params: compactParams(params) }),
  monthlyClosingChecklist: (id) => api.get(`/api/autopilot/cierre-mensual/${id}/`),
  updateMonthlyClosingChecklistItem: withOverviewInvalidation((id, payload = {}) => api.patch(`/api/autopilot/cierre-mensual/items/${id}/`, payload)),
  markMonthlyClosingChecklistReviewed: withOverviewInvalidation((id, payload = {}) => api.post(`/api/autopilot/cierre-mensual/${id}/mark-reviewed/`, payload)),
  consorcios: () => api.get("/consorcios/consorcios/"),
  periodosByConsorcio: (consorcioId, params = {}) => api.get(`/periodos/consorcios/${consorcioId}/periodos/`, { params: compactParams(params) }),
};


export default autopilotApi;
